import {
  categoryMapping,
  categorySubscriptionMapping,
  CreateProjectRequest,
  CreateProjectResponse,
  DecisionCategory,
  getAllDecisionTypes,
  getDecisionActionLabel,
  getEnabledDecisionTypes,
  ProjectListItem,
} from "../common/commonDomain";
import { failure, isOk, ok, pending, Result } from "../../common/common";
import { makeAutoObservable } from "mobx";
import { ApiService } from "../../api";
import { isEmpty, truncate } from "lodash";
import { History } from "history";
import { navigateToProject } from "../../utils/navigation";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { toLocalDateISOString } from "../../utils/Dates";
import { TFunction } from "../../i18n/tt";
import { MeetingType } from "../../meetings/meetingsDomain";
import { ProjectNavigationState } from "../project/projectDomain";
import { DecisionType } from "../../common/decisions";
import { DocumentType, getDocumentLabel } from "../../common/documents";
import { toProjectReference } from "../../utils/UrnReference";
import { ObjectPermission } from "../../types/Permissions";
import { EventDispatcher } from "../../events/eventsDomain";
import { StringFieldModel } from "../../common/commonForms";
import { PaidSubscription, SubscriptionType } from "../../common/subscription";
import { TabProps } from "../../components-v2/Tabs";
import { TMessageKey } from "../../i18n/types.gen";
import {
  CustomDocumentType,
  newCustomDocumentTypeLabels,
} from "../customDocument/customDocumentDomain";

export type ProjectTab = "all" | "by-me" | "by-others";

export type AddProjectParams = {
  projectName: string;
  decisions?: Array<DecisionType>;
  documents?: Array<DocumentType>;
  duplicateProjectId?: string;
  customDocumentType?: CustomDocumentType;
};

export class NewProjectController {
  documents: DocumentType[] = [];
  projectsResult: Result<ProjectListItem[]> = pending();
  projectToDelete: ProjectListItem | null = null;
  checkedDecisions: DecisionType[] = [];
  missingDecisions = false;
  createResult: Result | undefined;

  api: ApiService;
  history: History;
  paidSubscription: PaidSubscription | undefined;
  orgNo: string;
  messageStore: AlertMessageStore;
  dispatch: EventDispatcher;
  activeProjectTab: ProjectTab = "all";

  constructor({
    apiService,
    history,
    paidSubscription,
    messageStore,
    dispatch,
    orgNo,
  }: {
    apiService: ApiService;
    history: History;
    paidSubscription: PaidSubscription | undefined;
    messageStore: AlertMessageStore;
    dispatch: EventDispatcher;
    orgNo: string;
  }) {
    this.api = apiService;
    this.history = history;
    this.paidSubscription = paidSubscription;
    this.messageStore = messageStore;
    this.orgNo = orgNo;
    this.dispatch = dispatch;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get decisionsByCategory(): DecisionsByCategory[] {
    return getDecisionsByCategory({
      checkedDecisions: this.checkedDecisions,
      paidSubscription: this.paidSubscription,
      orgNo: this.orgNo,
    });
  }

  openAddProjectNameDialog(params: AddProjectParams) {
    this.dispatch([
      {
        type: "editProjectName",
        name: new StringFieldModel({ value: params.projectName, required: true }),
        save: (projectName: string) => {
          this.addProject({ ...params, projectName });
        },
        isDuplicate: params.duplicateProjectId !== undefined,
      },
    ]);
  }

  *addProject({
    projectName,
    decisions = this.checkedDecisions,
    documents,
    duplicateProjectId,
    customDocumentType,
  }: AddProjectParams) {
    if (isEmpty(decisions) && isEmpty(documents) && duplicateProjectId === undefined) {
      this.missingDecisions = true;
      this.createResult = failure();
      return;
    }

    const request: CreateProjectRequest = {
      orgNo: this.orgNo,
      projectName,
      duplicateProjectId,
      decisions,
      documents,
      customDocumentType,
    };
    this.createResult = pending();
    try {
      const response: CreateProjectResponse = yield this.api.newProject(request);
      this.createResult = ok();
      this.openProject(
        { id: response.projectId, organizationnumber: this.orgNo },
        { type: "project-navigation-state", from: "create-new" },
      );
    } catch (err) {
      this.messageStore.showGeneralError();
      this.createResult = failure();
    }
  }

  createProjectWithDocument(
    document: DocumentType,
    {
      t,
      showProjectNameDialog,
      customDocumentType,
    }: {
      t: TFunction;
      showProjectNameDialog?: boolean;
      customDocumentType?: CustomDocumentType;
    },
  ) {
    const addProjectParams: AddProjectParams = {
      projectName: createProjectName({ documents: [document], customDocumentType, t }),
      decisions: [],
      documents: [document],
      customDocumentType,
    };
    if (showProjectNameDialog) {
      this.openAddProjectNameDialog(addProjectParams);
    } else {
      this.addProject(addProjectParams);
    }
  }

  fetchProjects() {
    this.api
      .fetchProjects(this.orgNo)
      .then((res) => {
        this.setResult(ok(res.projects));
      })
      .catch((res) => {
        this.projectsResult = failure(res.error);
      });
  }

  openProject(
    { id, organizationnumber }: { id: string; organizationnumber: string },
    state?: ProjectNavigationState,
  ) {
    navigateToProject({ orgNo: organizationnumber, projectId: id, history: this.history, state });
  }

  setResult(projects: Result<ProjectListItem[]>) {
    this.projectsResult = projects;
  }

  selectForDelete(project: ProjectListItem | null) {
    this.projectToDelete = project;
  }

  openChangePermissionDialog(project: ProjectListItem) {
    this.dispatch([
      {
        type: "showChangePermissionDialog",
        title: project.projectName,
        objectId: toProjectReference(project.id),
        onUpdate: (permissions: ObjectPermission[]) =>
          (project.numberOfPermissions = permissions.length),
      },
    ]);
  }

  openDuplicateProjectDialog(project: ProjectListItem, projectName: string) {
    if (
      project.containsNoticeOrMinutesBoardMeetingDocument &&
      !project.containsNoticeOrMinutesGeneralMeetingDocument
    ) {
      this.dispatch([
        {
          type: "new-meeting",
          orgNo: this.orgNo,
          meetingType: MeetingType.BOARD_MEETING,
          projectName: { translated: projectName },
          projectId: project.id,
        },
      ]);
    } else if (
      !project?.containsNoticeOrMinutesBoardMeetingDocument &&
      project.containsNoticeOrMinutesGeneralMeetingDocument
    ) {
      this.dispatch([
        {
          type: "new-meeting",
          orgNo: this.orgNo,
          meetingType: MeetingType.GENERAL_MEETING,
          projectName: { translated: projectName },
          projectId: project.id,
        },
      ]);
    } else {
      this.openAddProjectNameDialog({ projectName, duplicateProjectId: project.id });
    }
  }

  toggleDecision(decision: DecisionType) {
    const index = this.checkedDecisions.indexOf(decision);
    if (index >= 0) {
      this.checkedDecisions.splice(index, 1);
    } else {
      this.checkedDecisions.push(decision);
    }
  }

  deleteSubmit() {
    if (!this.projectToDelete) return null;

    this.api.deleteProject(this.projectToDelete.id).then(() => {
      this.fetchProjects();
      this.selectForDelete(null);
      this.messageStore.showGeneralSuccess();
    });
  }

  get isShowDeleteDialog() {
    return Boolean(this.projectToDelete);
  }

  get containsProjects(): boolean {
    return isOk(this.projectsResult) && this.projectsResult.data.length > 0;
  }

  get projectTabs(): TabProps[] {
    const tabs: ProjectTab[] = ["all", "by-me", "by-others"];
    const labels: { [key in ProjectTab]: TMessageKey } = {
      all: "showAll",
      "by-me": "dbot.your-projects.by-me",
      "by-others": "dbot.your-projects.by-others",
    };

    return tabs.map<TabProps>((t) => ({
      id: t,
      label: { key: labels[t] },
      isActive: this.activeProjectTab === t,
      onSelect: () => (this.activeProjectTab = t),
    }));
  }

  get projects() {
    if (isOk(this.projectsResult)) {
      const data = this.projectsResult.data;
      switch (this.activeProjectTab) {
        case "all":
          return data;
        case "by-me":
          return data.filter((p) => p.isOwner);
        case "by-others":
          return data.filter((p) => !p.isOwner);
      }
    } else {
      return [];
    }
  }

  selectAllDecisions() {
    Object.values(DecisionType).forEach((k) => this.toggleDecision(DecisionType[k]));
  }
}

export const createProjectName = ({
  decisions,
  documents,
  customDocumentType,
  t,
}: {
  decisions?: DecisionType[];
  documents?: DocumentType[];
  customDocumentType?: CustomDocumentType;
  t: TFunction;
}): string => {
  const date = toLocalDateISOString(Date.now());

  if (decisions?.length) {
    return [
      date,
      truncate(decisions.map((d) => t(getDecisionActionLabel(d))).join(", "), {
        length: 150,
      }),
    ].join(" ");
  }

  if (documents?.length && customDocumentType) {
    return t(newCustomDocumentTypeLabels[customDocumentType]);
  }

  if (documents?.length) {
    return [date, t(getDocumentLabel(documents[0]))].join(" ");
  }

  return date;
};

export const groupDecisionTypesByCategory = (
  decisionTypes: DecisionType[],
): {
  category: DecisionCategory;
  requiredSubscriptionType: SubscriptionType;
  decisions: DecisionType[];
}[] => {
  return Object.values(DecisionCategory)
    .map((category) => ({
      category: category,
      requiredSubscriptionType: categorySubscriptionMapping[category],
      decisions: Object.entries(categoryMapping)
        .filter(([t, c]) => c === category && decisionTypes.includes(t as DecisionType))
        .map(([t]) => t as DecisionType),
    }))
    .filter(({ decisions }) => decisions.length > 0);
};

export type DecisionsByCategory = {
  category: DecisionCategory;
  decisions: {
    decisionType: DecisionType;
    isSelected: boolean;
    isDisabled: boolean;
    isExisting?: boolean;
  }[];
  requiredSubscriptionType: SubscriptionType;
  currentSubscription: PaidSubscription | undefined;
};

export const getDecisionsByCategory = ({
  checkedDecisions,
  paidSubscription,
  isBatchProject,
  orgNo,
  current,
}: {
  checkedDecisions: DecisionType[];
  paidSubscription: PaidSubscription | undefined;
  isBatchProject?: boolean;
  orgNo?: string;
  current?: DecisionType[];
}): DecisionsByCategory[] => {
  current = current || [];
  const decisionTypes = getAllDecisionTypes(orgNo);
  const enabledDecisionTypes = getEnabledDecisionTypes({ isBatchProject });
  return groupDecisionTypesByCategory(decisionTypes).map(
    ({ category, decisions, requiredSubscriptionType }) => ({
      category,
      decisions: decisions
        .filter((decisionType) => enabledDecisionTypes.includes(decisionType))
        .map((decisionType) => ({
          decisionType,
          isSelected: checkedDecisions.includes(decisionType),
          isDisabled: !enabledDecisionTypes.includes(decisionType),
          isExisting: current.includes(decisionType),
        })),
      currentSubscription: paidSubscription,
      requiredSubscriptionType,
    }),
  );
};
